import { useCallback, useState } from 'react';
import {
    CreateDTO,
    FullArticle,
    KBEntity,
    ModalFormMode,
    UpdateDTO,
    WithDraft,
} from '../admin-knowledge-base.type';
import { Form } from 'antd';
import { languages } from '../admin-knowledge-base.constants';
import dayjs from 'dayjs';
import { getById } from '../actions';
import { Types } from '/react4xp/parts/knowledge-base-common/knowledge-base-common.type';

interface Options<Entity extends KBEntity> {
    type: Types;
    handleCreate: (entity: CreateDTO<Entity>) => void;
    handleUpdate: (entity: UpdateDTO<Entity>) => void;
}

export const useModalForm = <Entity extends KBEntity>({
    type,
    handleCreate,
    handleUpdate,
}: Options<Entity>) => {
    const [mode, setMode] = useState<ModalFormMode>(ModalFormMode.CLOSE);
    const [form] = Form.useForm<WithDraft<Entity>>();
    const id = Form.useWatch('id', form);

    const onSubmit = (values: WithDraft<Entity>) => {
        const handler = mode === ModalFormMode.CREATE ? handleCreate : handleUpdate;

        const dto: any = { ...values };
        delete dto.draft;
        delete dto.sectionId;
        dto.linked = dto?.linked?.map((item) => item.value);

        if (dto.status) {
            if (!dto.status?.effective || !dto.status?.planned) {
                delete dto.status.effective;
                delete dto.status.planned;
            }
        }
        handler(dto);
        form.resetFields();
        hideForm();
    };

    const showUpdateForm = useCallback(async (entity: Entity) => {
        const formData =
            entity.type === Types.ARTICLE ? await getById<FullArticle>(entity.id) : entity;
        const status = {
            ...formData.status,
            ...(formData.status?.effective ? { effective: dayjs(formData.status?.effective) } : {}),
        };
        form.setFieldsValue({ ...formData, status, draft: formData[languages[0]] } as any); //TODO any
        setMode(ModalFormMode.UPDATE);
    }, []);

    const showCreateForm = useCallback((initialValues?: Record<string, string>) => {
        form.resetFields();
        form.setFieldsValue(initialValues as any);
        setMode(ModalFormMode.CREATE);
    }, []);

    const hideForm = useCallback(() => {
        setMode(ModalFormMode.CLOSE);
    }, []);

    return {
        id,
        type,
        form,
        mode,
        showCreateForm,
        showUpdateForm,
        hideForm,
        onSubmit,
    };
};
